// ============================================================
// Préréglages conventionnels — ce qu'une convention change dans les règles de paie
// ============================================================
//
// Choisir sa convention collective ne devrait pas obliger à ressaisir à la main les quelques
// chiffres qu'elle impose. Un préréglage porte les écarts CONNUS d'une convention par rapport
// au régime supplétif du code du travail, et l'écran de réglages propose de les appliquer.
//
// TROIS RÈGLES, parce qu'on touche à du droit :
//   1. rien n'est appliqué sans un clic explicite du gérant — jamais au simple choix de l'IDCC ;
//   2. chaque valeur est SOURCÉE dans le tableau ci-dessous, et affichée avant application ;
//   3. l'absence de préréglage n'empêche rien : tout reste saisissable champ par champ. Une
//      convention sans préréglage n'est pas une convention non supportée.
//
// AJOUTER UNE CONVENTION : relever les écarts dans le texte sur Légifrance (le lien est à un clic
// depuis l'écran), les inscrire ici avec leur article. Ne rien recopier d'un site tiers sans
// vérifier le texte lui-même.
//
// Deux natures d'écart, et elles ne s'appliquent pas au même endroit :
//   `patch`          des CHIFFRES dans les règles de paie (contingent, plafond, majorations). Ils
//                    remplissent le formulaire, et n'existent qu'une fois celui-ci enregistré.
//   `absence_types`  un CONGÉ que la convention crée. Il lui faut une entrée dans le référentiel
//                    des types d'absence, sans quoi personne ne peut le poser. Elle est écrite
//                    tout de suite (comme les autres référentiels de l'écran), jamais en double.

import { AbsenceQuota } from './hr-absence-catalog';
import { PayrollRules } from './hr-payroll';
import { RcrDecider } from './hr-worktime';

// Un écart conventionnel, prêt à afficher : ce qu'on change, la valeur, et le pourquoi.
export interface PresetItem {
	label: string;						// « Contingent annuel d'heures supplémentaires »
	display: string;					// « 130 h au lieu de 220 h »
	source: string;						// l'article ou l'avenant qui le dit
}

// Un CONGÉ que la convention crée et que la loi ne connaît pas — le jour d'enfant malade payé de
// la CCN 1982, par exemple. Il ne se règle pas dans un formulaire : il faut un type d'absence dans
// le référentiel (c_values 23) pour que quelqu'un puisse le poser. Le préréglage propose donc de
// le CRÉER, avec son plafond ; il ne touche à rien s'il existe déjà.
export interface PresetAbsenceType {
	code: string;							// le profil du catalogue (hr-absence-catalog.ts)
	name: string;							// le nom proposé dans le référentiel
	color: string;
	quota: AbsenceQuota;
}

export interface IdccPreset {
	idcc: string;
	name: string;
	// Ce que l'application écrira dans les règles de paie.
	patch: Partial<Pick<PayrollRules,
		'contingent_year_h' | 'max_week_conventional_min' | 'complementary_limit_ratio' | 'rcr_enabled'> & { rcr_decider: RcrDecider }>;
	// Les types d'absence que la convention rend nécessaires, créés à l'application du préréglage.
	absence_types?: PresetAbsenceType[];
	items: PresetItem[];
}

export const HR_IDCC_PRESETS: IdccPreset[] = [
	{
		idcc: '1982',
		name: 'Négoce et prestations de services dans les domaines médico-techniques',
		patch: {
			contingent_year_h: 130,
			max_week_conventional_min: 2760,		// 46 h
			complementary_limit_ratio: 1 / 3,
			rcr_enabled: true,
			rcr_decider: 'half',
		},
		absence_types: [
			{
				code: 'enfant_malade_paye',
				name: 'Enfant malade rémunéré (convention)',
				color: '#26a69a',
				quota: { days: 1, period: 'civil', seniority_months: 24 },
			},
		],
		items: [
			{
				label: 'Contingent annuel d\'heures supplémentaires',
				display: '130 h par an et par salarié, au lieu des 220 h supplétives',
				source: 'CCN 1982 (brochure 3286) — accord sur la durée du travail',
			},
			{
				label: 'Plafond hebdomadaire conventionnel',
				display: '46 h sur une semaine, au lieu des 48 h de l\'ordre public',
				source: 'CCN 1982 — durée du travail',
			},
			{
				label: 'Heures complémentaires du temps partiel',
				display: 'jusqu\'au tiers du contrat, au lieu du dixième supplétif',
				source: 'CCN 1982, accord de branche au sens de L3123-28',
			},
			{
				label: 'Repos compensateur de remplacement',
				display: 'en place, arbitré moitié par le salarié, moitié par l\'employeur',
				source: 'CCN 1982 § 2.9',
			},
			{
				label: 'Congé pour enfant malade',
				display: '1 jour RÉMUNÉRÉ par an, là où la loi n\'en paie aucun — sous 2 ans '
					+ 'd\'ancienneté, pour un enfant de 16 ans au plus, sur certificat médical',
				source: 'CCN 1982, accord du 22 mai 2014, art. 4 — s\'ajoute aux 3 à 5 jours non payés de L1225-61',
			},
		],
	},
];

export function hrIdccPresetOf(idcc: string | null | undefined): IdccPreset | null {
	const n = (idcc ?? '').toString().trim().replace(/^0+/, '');
	return HR_IDCC_PRESETS.find(p => p.idcc === n) ?? null;
}
